import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Badge, Banner, Button } from "@panelgenie/shared-ui";
import type {
  InvocationRecord,
  TestCaseDetail,
  TestCasePhoto,
  TestCaseRunSummary,
} from "../types.js";
import {
  formatInstant,
  kindLabel,
  outcomeBadgeVariant,
  outcomeLabel,
  parseSummary,
  photoRoleLabel,
} from "../lib/format.js";
import {
  breakerRows,
  filterRows,
  panelIdentityOf,
  sortRows,
  type FilterKey,
  type Sort,
  type SortKey,
} from "../lib/reads.js";
import { ReadsPanel } from "./ReadsPanel.js";

/**
 * TC-7 — one test case from `support.getTestCase`, the corpus's
 * regression unit. The page answers "what did the baseline say, and how
 * have re-runs compared?": the curator's notes, the durable baseline
 * photos, the immutable baseline breaker reads (reusing `lib/reads.ts`'s
 * shaping + the invocation-detail `ReadsPanel`), and the append-only run
 * history. A **Submit run** button re-runs the case through the async
 * scan-job pipeline; on success it reveals a "View in invocation list"
 * jump (the run lands as a Processing row at the top of `/`).
 *
 * The view is presentational + injectable: the route wires the real
 * `submitTestCaseRun` mutation and the two navigations (a run's produced
 * invocation, and the post-submit list jump), so the surface is testable
 * without a tRPC client or a RouterProvider.
 */

export type TestCaseDetailViewProps = {
  readonly detail: TestCaseDetail;
  /**
   * Re-run this case through the scan-job pipeline (`submitTestCaseRun`).
   * Resolves once the job is submitted (the run is then a Processing row).
   */
  readonly submitRun: () => Promise<unknown>;
  /** Navigate to a run's produced invocation detail (`/invocations/$id`). */
  readonly onSelectInvocation: (invocationId: string) => void;
  /** Navigate to the invocation list, where the new run appears on top. */
  readonly onViewList: () => void;
};

export function TestCaseDetailView({
  detail,
  submitRun,
  onSelectInvocation,
  onViewList,
}: TestCaseDetailViewProps) {
  const { testCase, parse, suggestions, photos, runs } = detail;
  const parseFailed = parse.status !== "ok";

  const identity = useMemo(() => panelIdentityOf(suggestions), [suggestions]);
  const allRows = useMemo(
    () => breakerRows(suggestions, identity),
    [suggestions, identity],
  );

  const [sort, setSort] = useState<Sort>({ key: "circuit", dir: 1 });
  const [filter, setFilter] = useState<FilterKey>("all");
  const [selectedBreakerId, setSelectedBreakerId] = useState<string | null>(null);
  const rows = useMemo(
    () => sortRows(filterRows(allRows, filter), sort),
    [allRows, filter, sort],
  );
  const handleSortChange = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 },
    );

  // `ReadsPanel` only reaches into `invocation.rawResponse` (its raw-JSON
  // and parse-failed panes); the baseline snapshot is exactly that, so a
  // minimal record carrying the snapshotted response drives it without a
  // full invocation row.
  const baselineInvocation = useMemo(
    () =>
      ({ id: testCase.id, rawResponse: testCase.baselineResponse }) as InvocationRecord,
    [testCase.id, testCase.baselineResponse],
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // True once a run is submitted this session — reveals the list jump.
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = () => {
    setSubmitting(true);
    setSubmitError(null);
    submitRun()
      .then(() => setSubmitted(true))
      .catch((err: unknown) =>
        setSubmitError(err instanceof Error ? err.message : "Submit failed."),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-6xl flex-col gap-6 overflow-y-auto px-4 py-6">
      <div>
        <a href="/test-cases" className="text-sm text-accent hover:underline">
          ← Back to test cases
        </a>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl font-semibold text-fg">
            {testCase.name}
          </h1>
          <Badge variant="neutral">{kindLabel(testCase.kind)}</Badge>
          <span className="text-sm text-mute">{parseSummary(parse)}</span>
        </div>
        <p className="mt-1 text-sm text-mute">
          Saved {formatInstant(testCase.createdAt)} from invocation{" "}
          <span className="font-mono">{testCase.baseInvocationId}</span>
        </p>
      </div>

      {submitError !== null ? (
        <Banner variant="danger" title="Submit failed">
          {submitError}
        </Banner>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={submitting}
          aria-label="submit-run"
          title="Re-run this case through the scan-job pipeline."
        >
          {submitting ? "Submitting…" : "Submit run"}
        </Button>
        {submitted ? (
          <Button
            variant="secondary"
            onClick={onViewList}
            aria-label="view-in-invocation-list"
            title="The run is a Processing row at the top of the invocation list."
          >
            View in invocation list
          </Button>
        ) : null}
      </div>

      <Section title="Notes">
        {testCase.notes.trim() === "" ? (
          <p className="text-sm text-mute">No notes recorded for this case.</p>
        ) : (
          <p className="whitespace-pre-wrap text-sm text-fg">{testCase.notes}</p>
        )}
      </Section>

      <Section title={`Baseline photos · ${photos.length}`}>
        <PhotoStrip photos={photos} />
      </Section>

      <Section title="Baseline reads">
        <div className="h-[520px] overflow-hidden rounded-lg border border-line">
          <ReadsPanel
            rows={rows}
            totalReads={allRows.length}
            identity={identity}
            invocation={baselineInvocation}
            parseFailed={parseFailed}
            selectedBreakerId={selectedBreakerId}
            onSelectBreaker={setSelectedBreakerId}
            sort={sort}
            onSortChange={handleSortChange}
            filter={filter}
            onFilterChange={setFilter}
          />
        </div>
      </Section>

      <Section title={`Run history · ${runs.length}`}>
        <RunHistory runs={runs} onSelectInvocation={onSelectInvocation} />
      </Section>
    </div>
  );
}

function PhotoStrip({ photos }: { readonly photos: readonly TestCasePhoto[] }) {
  if (photos.length === 0) {
    return <p className="text-sm text-mute">No baseline photos.</p>;
  }
  return (
    <div className="flex flex-wrap gap-3">
      {photos.map((photo) => (
        <figure
          key={photo.key}
          aria-label={`baseline-photo ${photo.sourcePhotoId}`}
          className="flex w-40 flex-col gap-1.5"
        >
          <img
            src={photo.url}
            alt={`Baseline photo ${photo.sourcePhotoId}`}
            className="aspect-square w-full rounded-md border border-line object-cover"
          />
          <figcaption className="flex items-center justify-between gap-2 text-xs text-mute">
            <span className="truncate font-mono" title={photo.sourcePhotoId}>
              {photo.sourcePhotoId}
            </span>
            <Badge variant="neutral">{photoRoleLabel(photo.role)}</Badge>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function RunHistory({
  runs,
  onSelectInvocation,
}: {
  readonly runs: readonly TestCaseRunSummary[];
  readonly onSelectInvocation: (invocationId: string) => void;
}) {
  if (runs.length === 0) {
    return (
      <p aria-label="runs-empty" className="text-sm text-mute">
        No runs yet. Submit one to regress this case against its baseline.
      </p>
    );
  }
  // Newest run first — the freshest comparison against the baseline.
  const ordered = [...runs].reverse();
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table aria-label="runs-table" className="w-full text-left text-sm">
        <thead className="border-b border-line bg-subtle text-mute">
          <tr>
            <Th>Submitted</Th>
            <Th>Outcome</Th>
            <Th>Provider</Th>
            <Th>
              <span className="sr-only">Invocation</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((run) => {
            const inv = run.invocation;
            return (
              <tr key={run.runId} className="border-b border-line last:border-b-0">
                <Td className="whitespace-nowrap text-mute">
                  {formatInstant(new Date(run.submittedAtMs).toISOString())}
                </Td>
                <Td>
                  <Badge variant={outcomeBadgeVariant(inv?.outcome ?? "processing")}>
                    {outcomeLabel(inv?.outcome ?? "processing")}
                  </Badge>
                </Td>
                <Td className="whitespace-nowrap text-mute">
                  {inv !== null ? `${inv.provider} · ${inv.modelId}` : "—"}
                </Td>
                <Td>
                  {inv !== null ? (
                    <button
                      type="button"
                      onClick={() => onSelectInvocation(inv.id)}
                      aria-label={`View invocation ${inv.id}`}
                      className="text-accent hover:underline"
                    >
                      View invocation →
                    </button>
                  ) : (
                    <span className="text-mute">pending</span>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2.5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-mute">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Th({ children }: { readonly children: ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return <td className={`px-3 py-2 align-middle ${className ?? ""}`}>{children}</td>;
}
